/**
 * Compliance.tsx — Compliance Autopilot overview page (Layer 4).
 *
 * Shows compliance score cards per framework, allows running new assessments,
 * and downloading PDF audit reports.
 *
 * Role behaviour:
 *   customer    — sees their own org's compliance data (user.customer_id)
 *   engineer /
 *   super_admin — sees a customer selector at the top; data loads for the
 *                 selected customer so admins can review any org's posture
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Framework {
  id: string;
  name: string;
  description: string;
  version: string;
  active: boolean;
}

interface Assessment {
  id: string;
  overall_score: number;
  status: string;
  total_controls: number;
  passed_controls: number;
  failed_controls: number;
  assessment_date: string;
  framework_name: string;
}

interface Customer {
  id: string;
  name: string;
  organisation: string;
}

// ── Score helpers ─────────────────────────────────────────────────────────────

function scoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

function scoreHex(score: number): string {
  if (score >= 80) return '#2ea55e';
  if (score >= 60) return '#f2a71e';
  return '#d43e3e';
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Compliance() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  // Admin/engineer: can view any customer's compliance data via selector
  const isAdmin = user?.role === 'super_admin' || user?.role === 'engineer';

  const [customers,          setCustomers]          = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [frameworks,         setFrameworks]         = useState<Framework[]>([]);
  const [assessments,        setAssessments]        = useState<Record<string, Assessment>>({});
  const [loading,            setLoading]            = useState(true);
  const [running,            setRunning]            = useState<string | null>(null);
  const [toast,              setToast]              = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // The org ID used for all compliance API calls:
  //   • customer role  → their own customer_id
  //   • admin/engineer → the customer selected in the dropdown
  const effectiveOrgId: string | null =
    isAdmin ? selectedCustomerId : (user?.customer_id ?? null);

  // ── Fetch customer list for admin selector ────────────────────────────────

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get<Customer[]>(`${API_BASE}/v1/customers/`)
      .then((r) => {
        setCustomers(r.data);
        if (r.data.length > 0 && !selectedCustomerId) {
          setSelectedCustomerId(r.data[0].id);
        }
      })
      .catch(() => {
        setToast({ msg: 'Failed to load customer list', severity: 'error' });
      });
  }, [isAdmin]); // selectedCustomerId intentionally excluded — only auto-select on first load

  // ── Load frameworks (once) ────────────────────────────────────────────────

  const loadFrameworks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/v1/compliance/frameworks`);
      setFrameworks(res.data);
    } catch {
      setToast({ msg: 'Failed to load frameworks', severity: 'error' });
    }
  }, []);

  // ── Load assessments for current effectiveOrgId ───────────────────────────

  const loadAssessments = useCallback(async (fws: Framework[]) => {
    if (!effectiveOrgId) return;
    const results: Record<string, Assessment> = {};
    await Promise.allSettled(
      fws.map(async (fw) => {
        try {
          const res = await axios.get(
            `${API_BASE}/v1/compliance/assessment/${effectiveOrgId}/${fw.name}`
          );
          results[fw.name] = res.data;
        } catch {
          // No assessment yet — that's fine
        }
      })
    );
    setAssessments(results);
  }, [effectiveOrgId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadFrameworks();
      setLoading(false);
    })();
  }, [loadFrameworks]);

  // Re-load assessments whenever frameworks or effectiveOrgId changes
  useEffect(() => {
    if (frameworks.length > 0) {
      setAssessments({});
      loadAssessments(frameworks);
    }
  }, [frameworks, loadAssessments]);

  // ── Actions ───────────────────────────────────────────────────────────────

  async function runAssessment(frameworkName: string) {
    if (!effectiveOrgId) {
      setToast({ msg: 'No organisation selected', severity: 'error' });
      return;
    }
    setRunning(frameworkName);
    try {
      await axios.post(`${API_BASE}/v1/compliance/assess/${effectiveOrgId}/${frameworkName}`);
      setToast({ msg: `${frameworkName} assessment complete`, severity: 'success' });
      const res = await axios.get(
        `${API_BASE}/v1/compliance/assessment/${effectiveOrgId}/${frameworkName}`
      );
      setAssessments((prev) => ({ ...prev, [frameworkName]: res.data }));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Assessment failed';
      setToast({ msg, severity: 'error' });
    } finally {
      setRunning(null);
    }
  }

  async function downloadReport(assessmentId: string, frameworkName: string) {
    try {
      const res = await axios.get(
        `${API_BASE}/v1/compliance/report/${assessmentId}/pdf`,
        { responseType: 'blob' }
      );
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href     = url;
      link.download = `compliance-report-${frameworkName}-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setToast({ msg: 'Failed to download report', severity: 'error' });
    }
  }

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>Compliance Autopilot</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <VerifiedIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Compliance Autopilot</Typography>
          <Typography variant="body2" color="text.secondary">
            Automated compliance tracking across SOC2, GDPR, HIPAA, PCI-DSS, and ISO27001
          </Typography>
        </Box>
      </Box>

      {/* Admin: customer selector */}
      {isAdmin && customers.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <BusinessIcon sx={{ color: 'text.secondary' }} />
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={selectedCustomerId ?? ''}
              label="Customer"
              onChange={(e) => setSelectedCustomerId(e.target.value || null)}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Warning: only for non-admin customers without a linked org */}
      {!isAdmin && !effectiveOrgId && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your account is not linked to an organisation. Please contact your administrator.
        </Alert>
      )}

      {/* Framework cards */}
      <Grid container spacing={3}>
        {frameworks.map((fw) => {
          const assessment = assessments[fw.name];
          const score      = assessment?.overall_score ?? null;
          const isRunning  = running === fw.name;

          return (
            <Grid item xs={12} sm={6} md={4} key={fw.id}>
              <Card
                elevation={2}
                onClick={assessment ? () => navigate(`/compliance/${fw.name.toLowerCase()}`) : undefined}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: 4,
                  borderColor: score !== null ? scoreHex(score) : 'divider',
                  transition: 'all 0.2s',
                  cursor: assessment ? 'pointer' : 'default',
                  '&:hover': assessment
                    ? { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 6 }
                    : { boxShadow: 6 },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  {/* Title row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>{fw.name}</Typography>
                    <Chip label={`v${fw.version}`} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {fw.description.slice(0, 100)}…
                  </Typography>

                  {/* Score */}
                  {assessment ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Compliance Score</Typography>
                        <Typography
                          variant="h4"
                          fontWeight={800}
                          sx={{ color: scoreHex(score!) }}
                        >
                          {score}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={score!}
                        color={scoreColor(score!)}
                        sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={statusLabel(assessment.status)}
                          color={scoreColor(score!)}
                          size="small"
                        />
                        <Chip
                          label={`${assessment.passed_controls}/${assessment.total_controls} controls`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Last assessed: {new Date(assessment.assessment_date).toLocaleDateString()}
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <AssessmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No assessment yet</Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                    disabled={isRunning || !effectiveOrgId}
                    onClick={(e) => { e.stopPropagation(); runAssessment(fw.name); }}
                  >
                    {isRunning ? 'Running…' : 'Run Assessment'}
                  </Button>

                  {assessment && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={(e) => { e.stopPropagation(); downloadReport(assessment.id, fw.name); }}
                      >
                        Download PDF
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/compliance/${fw.name.toLowerCase()}`); }}
                      >
                        View Details
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
